/*
 * hp-cart-frequency — sets a cart line's subscription plan, in both directions.
 *
 * The <select> carries "One Time" (value "") alongside every plan on offer, so
 * subscribing, changing frequency and unsubscribing are one control and one
 * call. There is deliberately no second element for the one-time case: a button
 * that could only subscribe made a one-time line a different shape from a
 * subscribed one, and it could not undo itself.
 *
 * Tiny on purpose. `cart-items` in assets/cart.js already owns the fetch, the
 * Section Rendering list, the loading state, the error surface and the
 * `cartUpdate` publish; this only has to hand it a selling plan. Nothing here
 * touches the DOM of the line — the section comes back re-rendered from the
 * server, which is REQUIRED rather than tidy: /cart/change.js issues a NEW LINE
 * KEY when the selling plan changes, so any in-place patch would leave the row
 * pointing at a key the cart no longer has.
 */
class HpCartFrequency extends HTMLElement {
  connectedCallback() {
    this.select = this.querySelector('select');
    if (!this.select) return;

    this.previous = this.select.value;
    this.select.addEventListener('change', this.onChange);
  }

  disconnectedCallback() {
    this.select?.removeEventListener('change', this.onChange);
  }

  onChange = () => {
    const cartItems = this.closest('cart-items');
    const line = this.dataset.line;
    const quantity = this.dataset.quantity;

    // No `cart-items` ancestor means the cart JS is not on this page; leaving
    // the select alone is better than a half-applied change.
    if (!cartItems || typeof cartItems.updateQuantity !== 'function') {
      this.select.value = this.previous;
      return;
    }

    /*
     * `this.select.value` is the EMPTY STRING on the "One Time" option, and
     * that value has to reach the payload — it is what clears the plan.
     * updateQuantity tests `!== undefined` rather than truthiness for exactly
     * this reason; a truthiness check drops it and the line silently keeps the
     * plan the shopper just chose to leave.
     */
    cartItems.updateQuantity(line, quantity, null, this, { sellingPlan: this.select.value });
  };
}

if (!customElements.get('hp-cart-frequency')) {
  customElements.define('hp-cart-frequency', HpCartFrequency);
}
